import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import TenantForm from './TenantForm';
import tenantService from '@/services/tenantService';
import { CreateTenantDto, UpdateTenantDto } from '@/types/tenant.types';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import TenantCreationStagesModal, { CreationStage, StageStatus, SeedingConfig } from '@/components/tenants/TenantCreationStagesModal';
import { useMemo, useState } from 'react';

export default function TenantFormPage() {
  const { tenant_key } = useParams<{ tenant_key?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!tenant_key;

  // Stages modal state
  const [showStagesModal, setShowStagesModal] = useState(false);
  const [createdTenantKey, setCreatedTenantKey] = useState<string>('');
  const [generatedAdminPassword, setGeneratedAdminPassword] = useState<string>('');

  // Seeding config state (inline in stages modal)
  const [showSeedingConfigForm, setShowSeedingConfigForm] = useState(false);

  const [creationStages, setCreationStages] = useState<CreationStage[]>([
    {
      id: 'create_tenant',
      label: 'Create Tenant',
      description: 'Creating tenant in database with all configurations',
      status: 'pending'
    },
    {
      id: 'gitlab_ingress',
      label: 'GitLab Ingress',
      description: 'Adding tenant to Kubernetes ingress manifest',
      status: 'pending'
    },
    {
      id: 'cloudflare_dns',
      label: 'CloudFlare DNS',
      description: 'Creating CNAME records for tenant domains',
      status: 'pending'
    },
    {
      id: 'seed_data',
      label: 'Seed Data',
      description: 'Initializing tenant with default groups, roles, and admin user',
      status: 'pending'
    },
    {
      id: 'health_check',
      label: 'Health Check',
      description: 'Pinging tenant to verify accessibility',
      status: 'pending'
    }
  ]);

  // Helper to update stage status
  const updateStage = (stageId: string, status: StageStatus, details?: string, error?: string) => {
    setCreationStages(prev =>
      prev.map(stage =>
        stage.id === stageId
          ? { ...stage, status, details, error }
          : stage
      )
    );
  };

  // Helper to reset stages
  const resetStages = () => {
    setCreationStages(prev =>
      prev.map(stage => ({
        ...stage,
        status: 'pending',
        details: undefined,
        error: undefined
      }))
    );
  };

  // Sleep utility
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Retry utility with exponential backoff
  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxAttempts: number = 3,
    baseDelay: number = 5000,
    onRetry?: (attempt: number, maxAttempts: number) => void
  ): Promise<T> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        if (attempt === maxAttempts) {
          throw error;
        }
        const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff: 5s, 10s, 20s
        if (onRetry) {
          onRetry(attempt, maxAttempts);
        }
        await sleep(delay);
      }
    }
    throw new Error('Retry failed');
  };

  // Poll GitLab status
  const pollGitLabStatus = async (tenant_key: string) => {
    const maxAttempts = 15;
    const interval = 2000;

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const tenant = await tenantService.getTenantByKey(tenant_key);
        const gitlabStatus = tenant.integrations?.gitlab_ingress?.status;

        if (gitlabStatus === 'success') {
          const commitId = tenant.integrations.gitlab_ingress.commit_id;
          updateStage('gitlab_ingress', 'success', `Commit: ${commitId?.substring(0, 8)}`);
          return;
        } else if (gitlabStatus === 'failed') {
          updateStage('gitlab_ingress', 'failed', undefined, tenant.integrations.gitlab_ingress.error);
          return;
        }

        await sleep(interval);
      } catch (error) {
        console.error('Error polling GitLab status:', error);
      }
    }

    // Timeout
    updateStage('gitlab_ingress', 'failed', undefined, 'Timeout waiting for GitLab sync');
  };

  // Poll CloudFlare status
  const pollCloudFlareStatus = async (tenant_key: string) => {
    const maxAttempts = 15;
    const interval = 2000;

    for (let i = 0; i < maxAttempts; i++) {
      try {
        const tenant = await tenantService.getTenantByKey(tenant_key);
        const cloudflareStatus = tenant.integrations?.cloudflare_dns?.status;
        const recordCount = tenant.integrations?.cloudflare_dns?.records?.length || 0;

        if (cloudflareStatus === 'success') {
          updateStage('cloudflare_dns', 'success', `${recordCount} CNAME records created`);
          return;
        } else if (cloudflareStatus === 'partial') {
          updateStage('cloudflare_dns', 'success', `${recordCount} of 2 CNAME records created (partial)`);
          return;
        } else if (cloudflareStatus === 'failed') {
          updateStage('cloudflare_dns', 'failed', undefined, tenant.integrations.cloudflare_dns.error);
          return;
        }

        await sleep(interval);
      } catch (error) {
        console.error('Error polling CloudFlare status:', error);
      }
    }

    // Timeout
    updateStage('cloudflare_dns', 'failed', undefined, 'Timeout waiting for CloudFlare DNS');
  };

  // Fetch existing tenant data for edit mode
  const { data: tenant, isLoading, error } = useQuery({
    queryKey: ['tenant', tenant_key],
    queryFn: () => tenantService.getTenantByKey(tenant_key!),
    enabled: isEdit,
  });

  // Create mode: fetch management tenant as a template for pre-filling
  const {
    data: managementTemplate,
    isLoading: isTemplateLoading,
  } = useQuery({
    queryKey: ['tenant-management-template'],
    queryFn: () => tenantService.getManagementTenantInfo(),
    enabled: !isEdit,
  });

  // Create mutation with stages
  const createMutation = useMutation({
    mutationFn: async (data: CreateTenantDto) => {
      // Reset and show stages modal
      resetStages();
      setShowStagesModal(true);

      // Stage 1: Create Tenant
      updateStage('create_tenant', 'in_progress');

      try {
        const tenant = await tenantService.createTenant(data);
        updateStage('create_tenant', 'success', `Tenant created: ${tenant.tenant_key}`);
        setCreatedTenantKey(tenant.tenant_key);

        // Stage 2: Poll GitLab Ingress (runs in background on backend)
        updateStage('gitlab_ingress', 'in_progress');
        await pollGitLabStatus(tenant.tenant_key);

        // Stage 3: Poll CloudFlare DNS (runs in background on backend)
        updateStage('cloudflare_dns', 'in_progress');
        await pollCloudFlareStatus(tenant.tenant_key);

        // Wait for infrastructure to stabilize before seeding
        updateStage('seed_data', 'pending', 'Waiting for infrastructure to stabilize...');
        await sleep(30000); // 30 seconds wait

        // Show seeding config form inline in stages modal
        setShowSeedingConfigForm(true);

        // Wait for user to configure admin details
        const config = await new Promise<SeedingConfig>((resolve) => {
          // Store resolve function to be called when user submits form
          (window as any).__seedingConfigResolve = resolve;
        });

        // User provided config - hide form and continue with seeding
        setShowSeedingConfigForm(false);
        setGeneratedAdminPassword(config.admin_password);

        // Stage 4: Seed Data (with extended retry - 10 attempts at 10s intervals)
        updateStage('seed_data', 'in_progress');
        let seedAttempt = 0;

        try {
          // Try up to 10 times with 10-second intervals
          for (let i = 1; i <= 10; i++) {
            seedAttempt = i;
            updateStage('seed_data', 'in_progress', `Attempting to seed... (${i}/10)`);

            try {
              await tenantService.seedTenant(tenant.tenant_key, {
                full_name: config.full_name,
                admin_email: config.admin_email,
                admin_password: config.admin_password,
                clear_data_first: config.clear_data_first,
                copy_templates: config.copy_templates
              });

              // Success!
              updateStage('seed_data', 'success', `Admin user created | Email: ${config.admin_email} (Attempt ${i}/10)`);
              break;
            } catch (attemptError: any) {
              console.log(`[TenantFormPage] Seed attempt ${i}/10 failed:`, attemptError.message);

              // If this was the last attempt, throw the error
              if (i === 10) {
                throw attemptError;
              }

              // Wait 10 seconds before next attempt
              updateStage('seed_data', 'in_progress', `Waiting 10s before retry... (${i}/10 failed)`);
              await sleep(10000);
            }
          }
        } catch (seedError: any) {
          updateStage('seed_data', 'failed', undefined, `Failed to seed tenant after ${seedAttempt} attempts: ${seedError?.message}`);
          // Don't throw - continue with creation even if seeding fails
        }

        // Wait before health check to allow DNS propagation
        updateStage('health_check', 'pending', 'Waiting for DNS propagation...');
        await sleep(15000); // Additional 15 seconds for DNS

        // Stage 5: Health Check (with retry logic)
        updateStage('health_check', 'in_progress');
        try {
          const uiUrl = tenant.domains[0]?.admin_url;
          const serviceUrl = tenant.domains[0]?.api_url;

          if (!uiUrl || !serviceUrl) {
            updateStage('health_check', 'failed', undefined, 'Tenant missing domain URLs');
          } else {
            await retryWithBackoff(
              async () => {
                // Ping tenant-specific backend URL
                const servicePingResponse = await fetch(`${serviceUrl}/skillzen-srv/ping`, {
                  method: 'GET',
                  headers: { 'Accept': 'application/json' },
                  mode: 'cors',
                  cache: 'no-cache'
                });

                if (!servicePingResponse.ok) {
                  throw new Error(`Backend returned ${servicePingResponse.status}`);
                }

                const servicePing = await servicePingResponse.json();

                // Ping tenant-specific frontend URL
                const uiPingResponse = await fetch(`${uiUrl}/ping`, {
                  method: 'GET',
                  headers: { 'Accept': 'application/json' },
                  mode: 'cors',
                  cache: 'no-cache'
                });

                if (!uiPingResponse.ok) {
                  throw new Error(`Frontend returned ${uiPingResponse.status}`);
                }

                const uiPing = await uiPingResponse.json();

                return { servicePing, uiPing };
              },
              4, // Max 4 attempts
              10000, // Start with 10 second delay
              (attempt, maxAttempts) => {
                updateStage('health_check', 'in_progress', `Pinging tenant URLs... (Attempt ${attempt}/${maxAttempts})`);
              }
            ).then(({ servicePing, uiPing }) => {
              updateStage('health_check', 'success', `Tenant accessible! Frontend: ${uiPing.status || 'healthy'} | Backend: ${servicePing.status || 'healthy'}`);
            });
          }
        } catch (pingError: any) {
          updateStage('health_check', 'failed', undefined, pingError?.message || 'Platform URLs not accessible. Check network connection.');
          // Don't throw - creation is complete even if ping fails
        }

        return tenant;
      } catch (error: any) {
        updateStage('create_tenant', 'failed', undefined, error?.message || 'Failed to create tenant');
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Tenant created successfully!');
      queryClient.invalidateQueries(['tenants']);
      // Don't navigate immediately - let user see stages complete
      // Navigation happens when they click "Done" in modal
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create tenant');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateTenantDto) => tenantService.updateTenant(tenant_key!, data),
    onSuccess: (updatedTenant) => {
      toast.success('Tenant updated successfully');
      queryClient.invalidateQueries(['tenants']);
      queryClient.invalidateQueries(['tenant', tenant_key]);
      navigate(`/admin/tenants`);
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update tenant');
    },
  });

  const handleSubmit = async (data: CreateTenantDto | UpdateTenantDto) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data as UpdateTenantDto);
    } else {
      await createMutation.mutateAsync(data as CreateTenantDto);
    }
  };

  // Prepare initial data for edit mode.
  // Memoized so its identity is stable across incidental re-renders — TenantForm's
  // populate effect keys on initialData, so an unstable object here would re-reset
  // the form (and previously fed an infinite render loop).
  // NOTE: these hooks MUST stay above the early returns below — hooks have to run
  // unconditionally on every render, or the hook order changes once data loads and
  // React throws "Rendered more hooks than during the previous render".
  const editInitialData: UpdateTenantDto | undefined = useMemo(
    () => (isEdit && tenant ? {
      name: tenant.name,
      domains: tenant.domains,
      s3_config: tenant.s3_config,
      email_config: tenant.email_config,
      ai_config: tenant.ai_config,
      limits: tenant.limits,
      status: tenant.status,
      allowed_domains: tenant.allowed_domains,
      onesignal_config: tenant.onesignal_config,
    } : undefined),
    [isEdit, tenant]
  );

  // Prepare initial data for create mode — pre-fill from management tenant template,
  // but blank out tenant-specific identifiers (name, key, domains, allowed_domains, status reset).
  const tpl = managementTemplate?.tenant;
  const createInitialData: CreateTenantDto | undefined = useMemo(
    () => (!isEdit && tpl ? {
      name: '',
      tenant_key: '',
      domains: [{ admin_url: '', api_url: '' }],
      s3_config: { ...tpl.s3_config, bucket_name: '' },
      email_config: tpl.email_config,
      ai_config: tpl.ai_config,
      onesignal_config: tpl.onesignal_config,
      allowed_domains: [],
    } : undefined),
    [isEdit, tpl]
  );

  const initialData = isEdit ? editInitialData : createInitialData;

  // Loading state for edit mode OR create mode while fetching template
  if ((isEdit && isLoading) || (!isEdit && isTemplateLoading)) {
    return (
      <div className="space-y-6">
        <div className="card p-6">
          <SkeletonLoader type="title" className="mb-2" />
          <SkeletonLoader type="text" />
        </div>
        <div className="card p-6">
          <SkeletonLoader type="title" className="mb-4" />
          <div className="space-y-4">
            <SkeletonLoader type="custom" height="44px" />
            <SkeletonLoader type="custom" height="44px" />
            <SkeletonLoader type="custom" height="44px" />
          </div>
        </div>
      </div>
    );
  }

  // Error state for edit mode
  if (isEdit && error) {
    return (
      <div className="card p-6">
        <p className="text-red-600">Failed to load tenant data. Please try again.</p>
      </div>
    );
  }

  // Not found state for edit mode
  if (isEdit && !tenant) {
    return (
      <div className="card p-6">
        <p className="text-gray-600">Tenant not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="p-2.5 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer group"
            title="Back to Tenants List"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-900 transition-colors" />
          </button>
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl text-white">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Tenant' : 'Create New Tenant'}
            </h1>
            <p className="text-gray-600">
              {isEdit
                ? `Update configuration for ${tenant?.name}`
                : 'Configure a new tenant with multi-tenant settings'
              }
            </p>
            {isEdit && tenant && (
              <code className="text-sm text-gray-500 font-mono">{tenant.tenant_key}</code>
            )}
          </div>
        </div>
      </div>

      {/* Form */}
      <TenantForm
        initialData={initialData}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
        tenantKey={tenant?.tenant_key}
      />

      {/* Tenant Creation Stages Modal (with inline seeding config) */}
      <TenantCreationStagesModal
        isOpen={showStagesModal}
        tenantName={creationStages[0]?.details?.split(': ')[1] || 'New Tenant'}
        tenantKey={createdTenantKey}
        stages={creationStages}
        onClose={() => {
          setShowStagesModal(false);
          queryClient.invalidateQueries(['tenants']);
          navigate('/admin/tenants');
        }}
        canClose={
          // Allow close if all stages complete OR if any stage has failed
          creationStages.every(s => s.status === 'success' || s.status === 'failed' || s.status === 'skipped') ||
          creationStages.some(s => s.status === 'failed')
        }
        showSeedingConfig={showSeedingConfigForm}
        onSeedingConfigSubmit={(config) => {
          // Resume the creation flow with user's config
          if ((window as any).__seedingConfigResolve) {
            (window as any).__seedingConfigResolve(config);
            delete (window as any).__seedingConfigResolve;
          }
        }}
      />
    </div>
  );
}
